// Display · Checklist
//
// Sequential lifecycle list — one row per step with state-driven icon + color.
// Used as the "Stan wniosku" sidebar on EDPR request detail and the matching
// progress block on transaction detail.
//
// State map:
//   completed → green check-circle, muted label (done — recedes)
//   pending   → amber history clock, primary label (current step — emphasised)
//   locked    → faint lock, faint label (future step — quiet)
//
// Display tier — pure props in, JSX out. No state, no data, no chrome.
// Consumer wraps in `<Card>` when chrome is needed.

import type { ReactNode } from "react";
import { Icon, type IconName } from "@ds/display/Icon";
import { TextValue, type TextTone } from "@ds/display/TextValue";
import { Box } from "@ds/layout/Box";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";

// ─── Row state ────────────────────────────────────────────────────────────

export type ChecklistRowState = "completed" | "pending" | "locked";

export interface ChecklistRow {
  /** Stable React key + test selector suffix. */
  key: string;
  /** Polish label rendered next to the state icon. */
  label: ReactNode;
  /** Lifecycle state — drives icon + tone. */
  state: ChecklistRowState;
  /** Optional count badge right-aligned in the row. */
  count?: number;
}

// ─── Inputs (data props — the model) ──────────────────────────────────────

export interface ChecklistInput {
  /** Rows rendered in DOM order — must be the canonical lifecycle order. */
  rows: ChecklistRow[];
  /** `data-testid` on the root list. Inner rows get `${testId}-row-{key}`. */
  testId?: string;
}

// Display atom — no Output, no Actions.

export type ChecklistProps = ChecklistInput;

// ─── State → icon + tone map ──────────────────────────────────────────────

const STATE_ICON: Record<ChecklistRowState, IconName> = {
  completed: "checkCircle",
  pending:   "history",
  locked:    "lock",
};

const STATE_ICON_TONE: Record<ChecklistRowState, TextTone> = {
  completed: "green",
  pending:   "amber",
  locked:    "faint",
};

const STATE_LABEL_TONE: Record<ChecklistRowState, TextTone> = {
  completed: "muted",
  pending:   "primary",
  locked:    "faint",
};

// ─── Implementation ───────────────────────────────────────────────────────

export function Checklist({ rows, testId }: ChecklistProps) {
  return (
    <Stack gap="none" testId={testId}>
      {rows.map((row, i) => (
        <div
          key={row.key}
          data-state={row.state}
          data-testid={testId ? `${testId}-row-${row.key}` : undefined}
        >
          <Box
            size="sm"
            divider={i > 0 ? "top" : undefined}
          >
            <Inline gap="sm" align="center" fullWidth>
              <TextValue as="span" tone={STATE_ICON_TONE[row.state]}>
                <Icon name={STATE_ICON[row.state]} size="sm" tone="current" />
              </TextValue>
              <Box grow>
                <TextValue
                  size="sm"
                  weight={row.state === "pending" ? "medium" : "regular"}
                  tone={STATE_LABEL_TONE[row.state]}
                >
                  {row.label}
                </TextValue>
              </Box>
              {row.count != null && (
                <Box
                  tone="grey"
                  border
                  shape="pill"
                  size="xs"
                  shrink
                >
                  <TextValue size="xs" tone="muted" tabular>
                    {row.count}
                  </TextValue>
                </Box>
              )}
            </Inline>
          </Box>
        </div>
      ))}
    </Stack>
  );
}
